import { computed, inject, Injectable, signal } from '@angular/core';
import {
  AddAppliancesToBasketRequest,
  BasketApplianceItemQuoteRequest,
  BasketItemType,
  BasketService,
  SelectedAppliance,
} from '@basket';
import { CatalogueService } from '@catalogue';
import { CONFIG } from '@config';
import { getPrice } from '../../constants/multi-plan-appliance-details-form-fields.constant';
import { SelectedApplianceDetails } from '../../models/selected-appliance-details.interface';
import { formatSelectedApplianceToBasketApplianceItemQuoteRequest } from '../../utils/multi-plan.utils';

@Injectable({
  providedIn: 'root',
})
export class MultiPlanService {
  private readonly basketService = inject(BasketService);
  private readonly config = inject(CONFIG);
  private readonly catalogueService = inject(CatalogueService);

  // Private signals for state management
  private readonly _initialApplianceBrand = signal<string>(
    this.config.clientName,
  );
  readonly _registeredAppliance = signal<SelectedAppliance | null>(null);
  readonly _selectedAppliances = signal<SelectedAppliance[]>([]);
  readonly _selectedAppliancesDetails = signal<SelectedApplianceDetails[]>([]);
  readonly _total = signal(0);

  readonly singleApplianceLowRiskPrice = 5;
  readonly singleApplianceHighRiskPrice = 7.49;

  // Getters for signals
  readonly appliances = signal<SelectedAppliance[]>([
    {
      applianceCode: 'AW',
      applianceName: 'Washing Machine',
      isSelected: false,
      itemType: BasketItemType.Quote,
    },
    {
      applianceCode: 'FF',
      applianceName: 'Fridge Freezer',
      isSelected: false,
      itemType: BasketItemType.Quote,
    },
    {
      applianceCode: 'DW',
      applianceName: 'Dishwasher',
      isSelected: false,
      itemType: BasketItemType.Quote,
    },
    {
      applianceCode: 'TD',
      applianceName: 'Tumble Dryer',
      isSelected: false,
      itemType: BasketItemType.Quote,
    },
    {
      applianceCode: 'WD',
      applianceName: 'Washer Dryer',
      isSelected: false,
      itemType: BasketItemType.Quote,
    },
    {
      applianceCode: 'OV',
      applianceName: 'Oven (Built-In)',
      isSelected: false,
      itemType: BasketItemType.Quote,
    },
    {
      applianceCode: 'CK',
      applianceName: 'Cooker (Freestanding)',
      isSelected: false,
      itemType: BasketItemType.Quote,
    },
  ]);
  readonly buildYourBundleOpened = signal(false);
  readonly initialApplianceBrand = this._initialApplianceBrand.asReadonly();
  readonly registeredAppliance = this._registeredAppliance.asReadonly();
  readonly selectedAppliancesDetails =
    this._selectedAppliancesDetails.asReadonly();

  readonly selectedAppliances = computed<SelectedAppliance[]>(() =>
    this.appliances().filter((a) => a.isSelected),
  );

  readonly showSelectedAppliancesDisclaimer = computed<boolean>(() =>
    this.selectedAppliances().some(
      (a) =>
        a.applianceCode === 'OV' ||
        a.applianceCode === 'FF' ||
        a.applianceCode === 'CK',
    ),
  );

  readonly singleAppliancePrice = computed<number>(() =>
    this.catalogueService.lowRiskPurchasePrice()
      ? this.singleApplianceLowRiskPrice
      : this.singleApplianceHighRiskPrice,
  );

  readonly riskBand = computed<'low' | 'high'>(() =>
    this.catalogueService.lowRiskPurchasePrice() ? 'low' : 'high',
  );

  readonly total = computed<number>(() => {
    return this.selectedAppliances().length === 0
      ? this.singleAppliancePrice()
      : getPrice(this.riskBand(), this.selectedAppliances().length + 1);
  });

  readonly additionalAppliancePrice = computed<number>(() => {
    return (
      getPrice(this.riskBand(), this.selectedAppliances().length + 2) -
      this.total()
    );
  });

  readonly priceStartingFrom = computed<number>(() =>
    this.catalogueService.lowRiskPurchasePrice()
      ? getPrice('low', 2)
      : getPrice('high', 2),
  );

  readonly vm = computed(() => ({
    selectedAppliances: this.selectedAppliances(),
  }));

  setSelectedAppliancesDetails(appliances: SelectedApplianceDetails[]): void {
    this._selectedAppliancesDetails.set(appliances);
  }

  resetSelectedAppliancesDetails(): void {
    this._selectedAppliancesDetails.set([]);
  }

  readonly addAppliancesToBasketPayload =
    computed<AddAppliancesToBasketRequest>(() => {
      const appliances: BasketApplianceItemQuoteRequest[] =
        formatSelectedApplianceToBasketApplianceItemQuoteRequest(
          this.selectedAppliancesDetails(),
        );

      return {
        appliances,
        basketId: this.basketService.basketId(),
        itemId: this.basketService.basketItem().itemId,
        itemType: BasketItemType.Quote,
      };
    });

  private setInitialRegisteredAppliance(): void {
    this._registeredAppliance.set(
      this.basketService.initialRegisteredAppliance(),
    );
  }

  constructor() {
    this.setInitialRegisteredAppliance();
  }
}
